import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { of, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import { ListAttachment } from '@site-mate/dashpivot-shared-library';

import { ResourcesService } from 'app/shared/service/resources.service';

@Component({
  selector: 'cc-list-attachments-previewer',
  templateUrl: './list-attachments-previewer.component.html',
  styleUrls: ['./list-attachments-previewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListAttachmentPreviewerComponent implements OnChanges, OnDestroy, OnInit {
  private readonly destroy$ = new Subject<void>();
  @Input() attachments: ListAttachment[];
  @Input() selectedAttachment: ListAttachment;
  @Input() downloading = false;
  @Input() editable = true;
  @Output() readonly close = new EventEmitter<void>();
  @Output() readonly download = new EventEmitter<ListAttachment>();
  @Output() readonly delete = new EventEmitter<ListAttachment>();

  readonly state = {
    attachmentUrl: null as SafeResourceUrl,
    attachmentLoaded: false,
    urlResolved: false,
  };

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly domSanitizer: DomSanitizer,
    private readonly resourcesService: ResourcesService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if ('selectedAttachment' in changes) {
      this.setCurrentAttachment(changes.selectedAttachment.currentValue);
    }
  }

  ngOnInit() {
    this.setCurrentAttachment(this.selectedAttachment);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onAttachmentLoaded() {
    this.state.attachmentLoaded = true;
  }

  onChangeSelectedAttachment(attachment: ListAttachment) {
    this.setCurrentAttachment(attachment);
  }

  onDeleteSelectedAttachment() {
    this.delete.emit(this.selectedAttachment);
  }

  onDownloadSelectedAttachment() {
    this.download.emit(this.selectedAttachment);
  }

  onClose() {
    this.close.emit();
  }

  setCurrentAttachment(attachment: ListAttachment | undefined) {
    const values = this.attachments;
    const selectedAttachment = values.find((entry) => attachment && entry.fullUrl === attachment.fullUrl);
    this.state.urlResolved = false;
    this.state.attachmentLoaded = false;
    of((this.selectedAttachment = selectedAttachment))
      .pipe(
        filter((value) => !!value),
        takeUntil(this.destroy$),
        switchMap((filteredEntry) => {
          return this.resourcesService.getDownloadUrl(filteredEntry.fullUrl, {
            keyName: filteredEntry.filename,
            disposition: 'inline',
          });
        }),
      )
      .subscribe((viewUrl) => {
        this.selectedAttachment = selectedAttachment;
        this.state.attachmentUrl = selectedAttachment
          ? this.domSanitizer.bypassSecurityTrustResourceUrl(viewUrl)
          : null;
        this.state.urlResolved = true;
        this.changeDetectorRef.markForCheck();
      });
  }
}
